import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";

interface Props {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  /** Bloquea el toggle, para autorizaciones que ya quedaron registradas y no se pueden revertir. */
  disabled?: boolean;
  /**
   * Quita fondo, borde y padding, dejando solo el check y el texto. Es como el
   * diseño usa la fila dentro de una card que ya aporta su propia superficie
   * (ej. el bloque "Autorizaciones requeridas"); apilar dos superficies grises
   * ahí se ve como una card dentro de otra.
   */
  bare?: boolean;
  /**
   * Superficie de la fila. `neutral` (default): gris (`tokens.surface2`). `info`: el `.callout` del diseño, con el
   * fondo y borde de aviso de la marca (`tokens.infoBg` / `infoBorder`), como los checks de `DatosAccionista`.
   */
  tone?: "neutral" | "info";
}

/**
 * Replica `.consent-row` + `.check` del diseño: fila clickeable con check
 * cuadrado custom, para autorizaciones/consentimientos legales.
 */
export const ConsentRow = ({ checked, onChange, label, disabled = false, bare = false, tone = "neutral" }: Props) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      width: "100%",
      textAlign: "left",
      fontFamily: "Outfit",
      cursor: disabled ? "default" : "pointer",
      ...(bare
        ? { bgcolor: "transparent", border: "none", p: 0 }
        : {
            bgcolor: tone === "info" ? "tokens.infoBg" : "tokens.surface2",
            border: "1px solid",
            borderColor: tone === "info" ? "tokens.infoBorder" : "divider",
            borderRadius: "10px",
            p: "14px 16px",
          }),
    }}
  >
    <Box
      sx={{
        width: 16,
        height: 16,
        mt: "1px",
        flexShrink: 0,
        borderRadius: "5px",
        border: "1.5px solid",
        borderColor: checked ? "primary.main" : "tokens.borderStrong",
        bgcolor: checked ? "primary.main" : "background.paper",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all .12s ease",
        color: "#fff",
      }}
    >
      {/* Palomita de trazo del diseño (`I.Check`), no el ícono relleno de MUI. */}
      {checked && (
        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </Box>
    {/* `.consent-row .meta` del diseño: ocupa el resto de la fila para que el texto no se desacomode al
        partirse en varias líneas, en peso 500; lo marcado con `strong` va en azul marino. */}
    <Typography
      component="div"
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.55,
        color: "tokens.textSecondary",
        "& strong": { color: "primary.dark", fontWeight: 600 },
      }}
    >
      {label}
    </Typography>
  </Box>
);
